import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublicBusinesses, getCategories } from '../lib/api';
import { BusinessCard } from '../components/BusinessCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ArrowRight, Store, Users, Zap, MapPin } from 'lucide-react';

const CATEGORIES = [
  'All',
  'Creative & Event Services',
  'Beauty & Personal Care',
  'Food & Dining',
  'Retail & Shopping',
  'Auto Services'
];

const SUBCATEGORIES = {
  'Creative & Event Services': [
    'Photography & Videography',
    'Event Planning & Coordination',
    'DJ & Music Services',
    'Decor, Backdrops & Rentals',
    'Catering & Mobile Food',
    'Makeup Artists & Hair Stylists',
    'Content Creators & Social Media',
    'Graphic Design & Branding',
    'Creative Workshops & Classes'
  ],
  'Beauty & Personal Care': [
    'Hair Stylists (Women)',
    'Braiders & Loc Specialists',
    'Barbers',
    'Nail Technicians',
    'Makeup Artists'
  ],
  'Food & Dining': [
    'Restaurants',
    'Food Trucks',
    'Catering Services',
    'Grocery & Markets',
    'Bakeries & Desserts',
    'Coffee & Cafes',
    'Bars & Nightlife'
  ],
  'Retail & Shopping': [
    'Boutiques & Clothing',
    'Grocery Stores',
    'Specialty & Gift Shops',
    'Electronics & Tech',
    'Home & Furniture',
    'Health & Wellness Products'
  ],
  'Auto Services': [
    'Mechanics & Repair',
    'Auto Detailing',
    'Towing Services',
    'Tire Shops',
    'Oil Change & Maintenance',
    'Body Shops & Collision'
  ]
};

export default function Home() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubcategory, setSelectedSubcategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBusinesses();
  }, [selectedCategory, selectedSubcategory]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const category = selectedCategory === 'All' ? null : selectedCategory;
      const subcategory = selectedSubcategory === 'All' ? null : selectedSubcategory;
      const res = await getPublicBusinesses(category, subcategory, searchQuery);
      setBusinesses(res.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('All');
  };

  // Get subcategories for selected category
  const currentSubcategories = selectedCategory !== 'All' && SUBCATEGORIES[selectedCategory] 
    ? ['All', ...SUBCATEGORIES[selectedCategory]] 
    : [];

  return (
    <div className="min-h-screen noise-bg">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1602955909693-579ecebbf5bf?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight leading-none mb-6 animate-fade-in-up">
              Discover
              <span className="text-primary"> Tucson's </span>
              Best Local Businesses
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-fade-in-up animation-delay-100">
              Your curated directory of trusted local services. From dining to home services, 
              find the businesses that make Tucson shine.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3 mb-8 animate-fade-in-up animation-delay-200">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search businesses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 bg-card/50 border-border rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="h-14 px-8 rounded-xl" data-testid="search-btn">
                Search
              </Button>
            </form>

            {/* CTA */}
            <Link to="/signup" className="animate-fade-in-up animation-delay-300">
              <Button size="lg" className="rounded-full px-8 gap-2 glow-amber" data-testid="hero-cta">
                List Your Business
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Store, label: 'Local Businesses', description: 'Curated directory of Tucson-based services' },
              { icon: Users, label: 'Local Discovery', description: 'Connecting customers with trusted providers' },
              { icon: MapPin, label: 'Tucson, Arizona', description: 'Focused on one city — done right' },
              { icon: Zap, label: '24/7 Online Presence', description: 'Your business, always visible' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-lg font-semibold text-foreground">{stat.label}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Directory Section */}
      <section className="py-16 md:py-24" data-testid="directory-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">
                Browse Directory
              </h2>
              <p className="text-muted-foreground">
                {businesses.length} {businesses.length === 1 ? 'business' : 'businesses'} in Tucson
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-6" data-testid="category-filter">
            <div className="flex flex-wrap gap-3">
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`category-pill px-5 py-2 rounded-full text-sm font-medium border transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-transparent text-muted-foreground border-border hover:border-primary/50'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategory Filter - only show when a specific category is selected */}
          {currentSubcategories.length > 0 && (
            <div className="mb-10" data-testid="subcategory-filter">
              <div className="flex flex-wrap gap-2">
                {currentSubcategories.map((subcategory) => (
                  <button
                    key={subcategory}
                    onClick={() => setSelectedSubcategory(subcategory)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedSubcategory === subcategory
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'bg-transparent text-muted-foreground border-border/50 hover:border-accent/50'
                    }`}
                  >
                    {subcategory}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Business Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse rounded-2xl bg-card h-80" />
              ))}
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-20">
              <Store className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-xl font-medium mb-2">No businesses found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery
                  ? 'Try a different search term'
                  : 'Be the first to list your business in this category!'}
              </p>
              <Link to="/signup">
                <Button className="rounded-full">Get Listed</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="business-grid">
              {businesses.map((business) => (
                <BusinessCard 
                  key={business.id} 
                  business={business} 
                  referrerCategory={selectedCategory !== 'All' ? selectedCategory : null}
                  referrerSubcategory={selectedSubcategory !== 'All' ? selectedSubcategory : null}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-card to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join LocalEdge Tucson and connect with thousands of local customers 
            actively searching for services like yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-8 w-full sm:w-auto glow-amber" data-testid="cta-signup">
                Start Your Listing
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg" className="rounded-full px-8 w-full sm:w-auto" data-testid="cta-pricing">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-semibold">LocalEdge</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              © 2024 LocalEdge. Supporting local businesses in Tucson, Arizona.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
